export type PrimaryNav = "agents" | "connectors" | "channels" | "sites";

/**
 * Product-roster ids. Only a subset map to live Durable Objects today —
 * the rest are Matrix personas (`availability: "soon"`) until wired.
 * Never "GA4 agent" (GA4 is a connector under Analytics).
 */
export const AGENT_IDS = [
	"onboarding",
	"paid_media",
	"ga4_anomaly",
	"seo",
	"content",
	"social",
	"email",
	"creative",
	"cro",
	"local",
	"developer",
	"reporting",
] as const;

export type AgentId = (typeof AGENT_IDS)[number];

export type AgentRoom = {
	id: AgentId;
	/** First-name persona shown in Matrix. */
	name: string;
	/** Department / specialty — product metaphor, not the display name. */
	department: string;
	/** One-line specialty under the name. */
	title: string;
	lede: string;
	status: string;
	initials: string;
	/** Live chat vs roadmap persona on the desk. */
	availability: "live" | "soon";
	/**
	 * Optional portrait under `/portraits/{file}`.
	 * Do not use `/agents/*` — that path is reserved for the Agents SDK
	 * proxy to backend. Until set, UI uses initials + brand tint.
	 */
	avatar: string | null;
	/** Retina companion for large roster portraits. */
	avatar2x: string | null;
};

/** One conversation surface per specialist agent — not Discord-style rooms. */
export const AGENTS: readonly AgentRoom[] = [
	{
		id: "onboarding",
		name: "Jules",
		department: "Onboarding",
		title: "Company pack & first config",
		lede: "Warm guide — URL in, company profile out, before anyone spends.",
		status: "Ready to research",
		initials: "Ju",
		availability: "live",
		avatar: "/portraits/onboarding.png?v=4",
		avatar2x: "/portraits/onboarding@2x.png?v=4",
	},
	{
		id: "paid_media",
		name: "Miles",
		department: "Paid media",
		title: "Search campaigns & budgets",
		lede: "Measured media buyer — briefs and paused builds, never a surprise charge.",
		status: "Needs Google Ads",
		initials: "Mi",
		availability: "live",
		avatar: "/portraits/paid-media.png?v=4",
		avatar2x: "/portraits/paid-media@2x.png?v=4",
	},
	{
		id: "ga4_anomaly",
		name: "Nadia",
		department: "Analytics",
		title: "Traffic & conversion watch",
		lede: "Quiet watcher — catches weird dips. GA4 is her connector, not her name.",
		status: "Needs GA4",
		initials: "Na",
		availability: "live",
		avatar: "/portraits/analytics.png?v=4",
		avatar2x: "/portraits/analytics@2x.png?v=4",
	},
	{
		id: "seo",
		name: "Theo",
		department: "SEO",
		title: "Organic search & technical",
		lede: "Patient ranker — queries, crawl health, and pages worth fixing first.",
		status: "Live · Search Console",
		initials: "Th",
		availability: "live",
		avatar: "/portraits/seo.png?v=4",
		avatar2x: "/portraits/seo@2x.png?v=4",
	},
	{
		id: "content",
		name: "Iris",
		department: "Content",
		title: "Briefs, drafts, brand voice",
		lede: "Sharp writer — outlines and drafts that sound like you, not a bot.",
		status: "Coming soon",
		initials: "Ir",
		availability: "soon",
		avatar: "/portraits/content.png?v=4",
		avatar2x: "/portraits/content@2x.png?v=4",
	},
	{
		id: "social",
		name: "Sol",
		department: "Social",
		title: "Posts & calendar",
		lede: "Channel-native drafts — nothing goes live until you approve.",
		status: "Coming soon",
		initials: "So",
		availability: "soon",
		avatar: "/portraits/social.png?v=4",
		avatar2x: "/portraits/social@2x.png?v=4",
	},
	{
		id: "email",
		name: "Vera",
		department: "Email",
		title: "Journeys & campaigns",
		lede: "Lifecycle mind — sequences and sends with a human on the approve button.",
		status: "Coming soon",
		initials: "Ve",
		availability: "soon",
		avatar: "/portraits/email.png?v=4",
		avatar2x: "/portraits/email@2x.png?v=4",
	},
	{
		id: "creative",
		name: "Kai",
		department: "Creative",
		title: "Ads & asset briefs",
		lede: "Visual director — concepts and variants, never a blind publish.",
		status: "Coming soon",
		initials: "Ka",
		availability: "soon",
		avatar: "/portraits/creative.png?v=4",
		avatar2x: "/portraits/creative@2x.png?v=4",
	},
	{
		id: "cro",
		name: "Quinn",
		department: "CRO",
		title: "Tests & landing lifts",
		lede: "Hypothesis first — experiments that earn their way to the page.",
		status: "Coming soon",
		initials: "Qu",
		availability: "soon",
		avatar: "/portraits/cro.png?v=4",
		avatar2x: "/portraits/cro@2x.png?v=4",
	},
	{
		id: "local",
		name: "Dana",
		department: "Local",
		title: "GBP & nearby demand",
		lede: "Neighborhood radar — listings, reviews, and local search intent.",
		status: "Coming soon",
		initials: "Da",
		availability: "soon",
		avatar: "/portraits/local.png?v=4",
		avatar2x: "/portraits/local@2x.png?v=4",
	},
	{
		id: "developer",
		name: "Rhea",
		department: "Developer",
		title: "Sites, DNS & deploys",
		lede: "Repo, hosting, DNS, and deploys — production changes wait on you.",
		status: "Live · Sites",
		initials: "Rh",
		availability: "live",
		avatar: "/portraits/web.png?v=4",
		avatar2x: "/portraits/web@2x.png?v=4",
	},
	{
		id: "reporting",
		name: "Arlo",
		department: "Reporting",
		title: "Digests & narrative",
		lede: "Clear storyteller — what moved, why it matters, what to do next.",
		status: "Live · GA4 + Ads digest",
		initials: "Ar",
		availability: "live",
		avatar: "/portraits/reporting.png?v=4",
		avatar2x: "/portraits/reporting@2x.png?v=4",
	},
] as const;

export function agentById(id: AgentId): AgentRoom {
	const agent = AGENTS.find((entry) => entry.id === id);
	if (!agent) throw new Error(`Unknown agent: ${id}`);
	return agent;
}

export type IntegrationStatus = "connected" | "available" | "soon" | "attention";

/** Grouping for Connectors / Channels hubs — by job, not by vendor. */
export type IntegrationCategory =
	| "advertising"
	| "analytics"
	| "social"
	| "creative"
	| "cms"
	| "commerce"
	| "hosting"
	| "lifecycle"
	| "messaging"
	| "tasks";

export const CONNECTOR_CATEGORY_ORDER: readonly IntegrationCategory[] = [
	"advertising",
	"analytics",
	"social",
	"creative",
	"cms",
	"commerce",
	"hosting",
	"lifecycle",
] as const;

export const CHANNEL_CATEGORY_ORDER: readonly IntegrationCategory[] = [
	"messaging",
	"tasks",
] as const;

export const INTEGRATION_CATEGORY_LABEL: Record<IntegrationCategory, string> = {
	advertising: "Advertising",
	analytics: "Analytics & measurement",
	social: "Social",
	creative: "Creative assets",
	cms: "Sites & CMS",
	commerce: "Commerce",
	hosting: "Hosting & domains",
	lifecycle: "CRM & email",
	messaging: "Messaging",
	tasks: "Tasks & docs",
};

/** One-line purpose under each connectors section heading. */
export const INTEGRATION_CATEGORY_LEDE: Record<IntegrationCategory, string> = {
	advertising: "Ad accounts and campaign spend.",
	analytics: "Traffic, search, tags, and local signals.",
	social: "Organic brand accounts and publishing.",
	creative: "Brand files, photos, and design tools.",
	cms: "Site content and publishing.",
	commerce: "Catalog and store context.",
	hosting: "Repos, hosting, DNS, and domains.",
	lifecycle: "CRM, journeys, and email lists.",
	messaging: "Where approval requests reach your team.",
	tasks: "Approvals as tasks in your workspace.",
};

export type Integration = {
	id: string;
	/** Catalog id in `@mairko/brand` platform-icons (same as Hero / PlatformRail). */
	markId: string;
	name: string;
	blurb: string;
	status: IntegrationStatus;
	category: IntegrationCategory;
};

/** Platform OAuth / data sources. Icons from `@mairko/brand` platform-icons. */
export const CONNECTORS: readonly Integration[] = [
	{
		id: "google_ads",
		markId: "ads",
		name: "Google Ads",
		blurb: "Campaigns, budgets, Keyword Plan.",
		status: "available",
		category: "advertising",
	},
	{
		id: "linkedin_ads",
		markId: "linkedin",
		name: "LinkedIn Ads",
		blurb: "B2B ad accounts and campaigns.",
		status: "available",
		category: "advertising",
	},
	{
		id: "meta_ads",
		markId: "meta",
		name: "Meta Ads",
		blurb: "Later — after Search is solid.",
		status: "soon",
		category: "advertising",
	},
	{
		id: "tiktok",
		markId: "tiktok",
		name: "TikTok",
		blurb: "Short-form ads after Search.",
		status: "soon",
		category: "advertising",
	},
	{
		id: "youtube",
		markId: "youtube",
		name: "YouTube",
		blurb: "Video ads and channel signals.",
		status: "soon",
		category: "advertising",
	},
	{
		id: "google_drive",
		markId: "googledrive",
		name: "Google Drive",
		blurb: "Brand files and shared folders.",
		status: "available",
		category: "creative",
	},
	{
		id: "google_photos",
		markId: "googlephotos",
		name: "Google Photos",
		blurb: "Product and brand photo libraries.",
		status: "available",
		category: "creative",
	},
	{
		id: "canva",
		markId: "canva",
		name: "Canva",
		blurb: "Designs and brand kits.",
		status: "available",
		category: "creative",
	},
	{
		id: "figma",
		markId: "figma",
		name: "Figma",
		blurb: "Design files and brand systems.",
		status: "available",
		category: "creative",
	},
	{
		id: "dropbox",
		markId: "dropbox",
		name: "Dropbox",
		blurb: "Shared folders for creatives.",
		status: "available",
		category: "creative",
	},
	{
		id: "ga4",
		markId: "analytics",
		name: "Google Analytics 4",
		blurb: "Traffic and conversion signals.",
		status: "available",
		category: "analytics",
	},
	{
		id: "gsc",
		markId: "searchconsole",
		name: "Search Console",
		blurb: "Queries and index coverage.",
		status: "available",
		category: "analytics",
	},
	{
		id: "gtm",
		markId: "tagmanager",
		name: "Google Tag Manager",
		blurb: "Tags and conversion plumbing.",
		status: "available",
		category: "analytics",
	},
	{
		id: "gbp",
		markId: "gbp",
		name: "Google Business Profile",
		blurb: "Local listings and reviews.",
		status: "available",
		category: "analytics",
	},
	{
		id: "linkedin",
		markId: "linkedin",
		name: "LinkedIn",
		blurb: "Company Page for organic posts.",
		status: "available",
		category: "social",
	},
	{
		id: "x",
		markId: "x",
		name: "X",
		blurb: "Brand account for organic posts.",
		status: "available",
		category: "social",
	},
	{
		id: "pinterest",
		markId: "pinterest",
		name: "Pinterest",
		blurb: "Boards and pins for organic + shopping.",
		status: "available",
		category: "social",
	},
	{
		id: "instagram",
		markId: "instagram",
		name: "Instagram",
		blurb: "Organic + ads via Meta.",
		status: "soon",
		category: "social",
	},
	{
		id: "facebook",
		markId: "facebook",
		name: "Facebook",
		blurb: "Pages and Meta Ads delivery.",
		status: "soon",
		category: "social",
	},
	{
		id: "wordpress",
		markId: "wordpress",
		name: "WordPress",
		blurb: "Publish pages and posts.",
		status: "available",
		category: "cms",
	},
	{
		id: "webflow",
		markId: "webflow",
		name: "Webflow",
		blurb: "CMS and site publish.",
		status: "available",
		category: "cms",
	},
	{
		id: "wix",
		markId: "wix",
		name: "Wix",
		blurb: "Site content and SEO.",
		status: "available",
		category: "cms",
	},
	{
		id: "squarespace",
		markId: "squarespace",
		name: "Squarespace",
		blurb: "Commerce site + catalog.",
		status: "available",
		category: "cms",
	},
	{
		id: "shopify",
		markId: "shopify",
		name: "Shopify",
		blurb: "Catalog and storefront for commerce briefs.",
		status: "available",
		category: "commerce",
	},
	{
		id: "stripe",
		markId: "stripe",
		name: "Stripe",
		blurb: "Revenue signals for briefs.",
		status: "soon",
		category: "commerce",
	},
	{
		id: "github",
		markId: "github",
		name: "GitHub",
		blurb: "Repos and site code.",
		status: "available",
		category: "hosting",
	},
	{
		id: "vercel",
		markId: "vercel",
		name: "Vercel",
		blurb: "Preview and production deploys.",
		status: "available",
		category: "hosting",
	},
	{
		id: "cloudflare",
		markId: "cloudflare",
		name: "Cloudflare",
		blurb: "DNS, CDN, and Workers.",
		status: "available",
		category: "hosting",
	},
	{
		id: "godaddy",
		markId: "godaddy",
		name: "GoDaddy",
		blurb: "Domain registrar for SMBs.",
		status: "available",
		category: "hosting",
	},
	{
		id: "hubspot",
		markId: "hubspot",
		name: "HubSpot",
		blurb: "CRM and marketing automation.",
		status: "soon",
		category: "lifecycle",
	},
	{
		id: "klaviyo",
		markId: "klaviyo",
		name: "Klaviyo",
		blurb: "Email and SMS journeys.",
		status: "soon",
		category: "lifecycle",
	},
	{
		id: "mailchimp",
		markId: "mailchimp",
		name: "Mailchimp",
		blurb: "Campaigns and audiences.",
		status: "soon",
		category: "lifecycle",
	},
] as const;

/** Messaging / task binds for approvals. Icons from `@mairko/brand`. */
export const COMMS_CHANNELS: readonly Integration[] = [
	{
		id: "slack",
		markId: "slack",
		name: "Slack",
		blurb: "Approval cards and agent threads.",
		status: "available",
		category: "messaging",
	},
	{
		id: "whatsapp",
		markId: "whatsapp",
		name: "WhatsApp",
		blurb: "Client-initiated window; templates outside it.",
		status: "soon",
		category: "messaging",
	},
	{
		id: "email",
		markId: "gmail",
		name: "Email",
		blurb: "Gmail OAuth — magic-link confirm, never on GET.",
		status: "available",
		category: "messaging",
	},
	{
		id: "teams",
		markId: "teams",
		name: "Microsoft Teams",
		blurb: "Approvals in the workspace you already use.",
		status: "soon",
		category: "messaging",
	},
	{
		id: "discord",
		markId: "discord",
		name: "Discord",
		blurb: "Server threads for agent updates.",
		status: "soon",
		category: "messaging",
	},
	{
		id: "telegram",
		markId: "telegram",
		name: "Telegram",
		blurb: "Bot alerts and approve via deep link.",
		status: "soon",
		category: "messaging",
	},
	{
		id: "twilio",
		markId: "twilio",
		name: "SMS",
		blurb: "Short alerts with signed confirm links.",
		status: "soon",
		category: "messaging",
	},
	{
		id: "intercom",
		markId: "intercom",
		name: "Intercom",
		blurb: "Inbox for client conversations.",
		status: "soon",
		category: "messaging",
	},
	{
		id: "clickup",
		markId: "clickup",
		name: "ClickUp",
		blurb: "Approval as a task — status is the decision.",
		status: "soon",
		category: "tasks",
	},
	{
		id: "asana",
		markId: "asana",
		name: "Asana",
		blurb: "Task-based approvals for project teams.",
		status: "soon",
		category: "tasks",
	},
	{
		id: "trello",
		markId: "trello",
		name: "Trello",
		blurb: "Cards for work that needs your review.",
		status: "soon",
		category: "tasks",
	},
	{
		id: "linear",
		markId: "linear",
		name: "Linear",
		blurb: "Issue status for eng-adjacent approvals.",
		status: "soon",
		category: "tasks",
	},
	{
		id: "notion",
		markId: "notion",
		name: "Notion",
		blurb: "Shared docs and decision pages.",
		status: "soon",
		category: "tasks",
	},
] as const;
